import { copiarTexto, textoParaCopiar } from '../services/versus';
import { t } from '../services/textos';

const ICONES = { ia: 'assets/versus/icon-ia.png', eu: 'assets/versus/icon-usuario.png' };
const SVG = 'http://www.w3.org/2000/svg';
/** Ícones em pixel (16×16): duas folhas sobrepostas (copiar) e o visto (copiado). */
const DESENHOS = {
    copiar: 'M5 1h10v11h-2V3H5z M1 4h10v11H1z M3 6v7h6V6z',
    copiado: 'M13 3h2v2h-1v1h-1v1h-1v1h-1v1h-1v1H9v1H8v1H7v1H5v-1H4v-1H3V9H2V7h2v1h1v1h1v1h1V9h1V8h1V7h1V6h1V5h1V4h1z'
};
/** Quanto tempo o visto fica no lugar do ícone depois de copiar. */
const TEMPO_DO_VISTO_MS = 1800;

function iconeSvg (desenho) {
    const svg = document.createElementNS(SVG, 'svg');
    svg.setAttribute('viewBox', '0 0 16 16');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('shape-rendering', 'crispEdges');
    const caminho = document.createElementNS(SVG, 'path');
    caminho.setAttribute('d', DESENHOS[desenho]);
    caminho.setAttribute('fill', 'currentColor');
    caminho.setAttribute('fill-rule', 'evenodd');
    svg.append(caminho);
    return svg;
}

/**
 * Conversa com a IA do Modo Versus (Figma, quadro 74), em camada DOM sobre
 * o canvas: rola com o dedo, quebra texto longo e deixa selecionar a resposta.
 *
 * O texto entra sempre como nó de texto — nada do que a IA ou o
 * participante escrevem é interpretado como HTML. Só `**trecho**` vira
 * destaque.
 */
export class ChatVersus {
    constructor (scene, { x, y, largura, altura }) {
        this.elemento = document.createElement('div');
        this.elemento.className = 'chat-versus';
        this.elemento.style.width = `${largura}px`;
        this.elemento.style.height = `${altura}px`;
        this.elemento.setAttribute('role', 'log');
        this.elemento.setAttribute('aria-label', 'Conversa com a IA');
        this.dom = scene.add.dom(x + largura / 2, y + altura / 2, this.elemento);
        this.espera = null;
    }

    static paragrafo (texto) {
        const p = document.createElement('p');
        String(texto).split('**').forEach((trecho, i) => {
            if (trecho === '') return;
            if (i % 2 === 1) {
                const forte = document.createElement('strong');
                forte.textContent = trecho;
                p.append(forte);
            } else {
                p.append(document.createTextNode(trecho));
            }
        });
        return p;
    }

    linha (classe, autor, paragrafo) {
        const linha = document.createElement('div');
        linha.className = `mensagem ${classe}`;
        const icone = document.createElement('img');
        icone.src = ICONES[autor];
        icone.alt = autor === 'ia' ? 'IA' : 'Você';
        linha.append(icone, paragrafo);
        this.elemento.append(linha);
        this.rolarAoFim();
        return linha;
    }

    /**
     * Botão de copiar uma resposta da IA (canto direito da linha). Copia o texto sem as marcas
     * `**`; o ícone vira um visto por um instante, ou fica vermelho se o aparelho não copiar.
     */
    static botaoCopiar (texto) {
        const botao = document.createElement('button');
        botao.type = 'button';
        botao.className = 'copiar';
        botao.setAttribute('aria-label', t('versus.copiar_resposta'));
        botao.title = t('versus.copiar_resposta');
        botao.append(iconeSvg('copiar'));
        let volta = null;
        botao.addEventListener('click', async () => {
            const copiou = await copiarTexto(textoParaCopiar(texto));
            clearTimeout(volta);
            botao.classList.toggle('copiado', copiou);
            botao.classList.toggle('falhou', !copiou);
            botao.replaceChildren(iconeSvg(copiou ? 'copiado' : 'copiar'));
            botao.setAttribute('aria-label', copiou ? t('versus.copiado') : t('versus.erro_copiar'));
            botao.title = botao.getAttribute('aria-label');
            volta = setTimeout(() => {
                botao.classList.remove('copiado', 'falhou');
                botao.replaceChildren(iconeSvg('copiar'));
                botao.setAttribute('aria-label', t('versus.copiar_resposta'));
                botao.title = t('versus.copiar_resposta');
            }, TEMPO_DO_VISTO_MS);
        });
        return botao;
    }

    /**
     * @param {{autor: 'ia'|'eu', texto: string, anexos?: string[], copiavel?: boolean}} mensagem
     *   `copiavel`: mostra o botão de copiar (padrão: só nas respostas da IA).
     */
    adicionar ({ autor, texto, anexos = [], copiavel = autor === 'ia' }) {
        const p = ChatVersus.paragrafo(texto);
        if (anexos.length > 0) {
            // RN-MV-08: o material anexado fica visível junto da mensagem.
            const etiqueta = document.createElement('span');
            etiqueta.className = 'anexos';
            etiqueta.textContent = `Anexo: ${anexos.join(' • ')}`;
            p.append(etiqueta);
        }
        const linha = this.linha(autor, autor, p);
        if (copiavel) linha.append(ChatVersus.botaoCopiar(texto));
        return linha;
    }

    /** Remove as linhas do histórico (mantém aviso, espera e DOM) para a cena redesenhar. */
    limpar () {
        this.elemento.querySelectorAll('.mensagem:not(.espera)').forEach((linha) => linha.remove());
    }

    /** Estado de carregamento enquanto a IA responde: sem ele, parece travado. */
    mostrarEspera () {
        this.ocultarEspera();
        const p = document.createElement('p');
        p.textContent = '...';
        this.espera = this.linha('ia espera', 'ia', p);
    }

    ocultarEspera () {
        this.espera?.remove();
        this.espera = null;
    }

    avisar (texto) {
        this.limparAviso();
        this.aviso = document.createElement('div');
        this.aviso.className = 'aviso';
        this.aviso.textContent = texto;
        this.elemento.append(this.aviso);
        this.rolarAoFim();
    }

    limparAviso () {
        this.aviso?.remove();
        this.aviso = null;
    }

    rolarAoFim () {
        this.elemento.scrollTop = this.elemento.scrollHeight;
    }

    setVisivel (visivel) {
        this.dom.setVisible(visivel);
    }
}
